import { z } from "zod";

/** Background job model shared by server and web (admin jobs API). */

export const JOB_STATUSES = ["queued", "running", "succeeded", "failed", "cancelled"] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

export const TERMINAL_JOB_STATUSES = ["succeeded", "failed", "cancelled"] as const;

export function isTerminalJobStatus(status: JobStatus): boolean {
  return (TERMINAL_JOB_STATUSES as readonly string[]).includes(status);
}

export const JOB_TYPES = [
  "calibre_import",
  "koreader_digest_backfill",
  "author_suggestions_refresh",
  "author_suggestions_sweep",
  "author_mapping_automap",
  "author_suggestions_sync",
] as const;
export type JobType = (typeof JOB_TYPES)[number];

export const JOB_LOG_LEVELS = ["info", "warn", "error"] as const;
export type JobLogLevel = (typeof JOB_LOG_LEVELS)[number];

/** Maximum number of log entries kept per job (further `info` entries are dropped). */
export const JOB_LOG_CAP = 50_000;
export const JOB_LOG_PAGE_MAX = 200;
export const JOB_RETENTION_DAYS = 90;

/** `GET /api/admin/jobs/koreader-digest-backfill`: state for the manual backfill action. */
export interface KoreaderBackfillStatusDto {
  /** Stored files without a KOReader document id. */
  filesWithoutDigest: number;
  /** The queued or running backfill job, if any. */
  activeJobId: string | null;
}

export interface JobProgressDto {
  phase: string;
  processed: number;
  total: number | null;
  /** Name of the item being processed (the author of a sync run). */
  current?: string;
  /** Running result of the job (sync: the count fields of `SyncJobResult`). */
  totals?: Record<string, number>;
  /** Info log entries dropped so far because the log cap was reached (persisted for resumes). */
  droppedLogEntries?: number;
}

export interface JobDto {
  id: string;
  type: string;
  status: JobStatus;
  payload: unknown;
  progress: JobProgressDto | null;
  result: unknown;
  /** An error code (for example `storage_full`) or an English message. */
  error: string | null;
  cancelRequested: boolean;
  createdBy: { id: string; name: string } | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface JobListDto {
  jobs: JobDto[];
  total: number;
  page: number;
  pageSize: number;
}

export interface JobLogEntryDto {
  id: number;
  at: string;
  level: JobLogLevel;
  /** English fallback; the UI renders `context.code` through the `jobs.log.<code>` translations. */
  message: string;
  context: Record<string, unknown> | null;
}

export interface JobLogDto {
  entries: JobLogEntryDto[];
  /** True when more entries exist after the last returned one. */
  hasMore: boolean;
}

const levelList = z
  .string({ error: "invalid" })
  .transform((value) => value.split(",").filter(Boolean))
  .pipe(z.array(z.enum(JOB_LOG_LEVELS, { error: "invalid" })).min(1, { error: "invalid" }));

export const jobListQuerySchema = z.object({
  type: z.string({ error: "invalid" }).max(64, { error: "too_long" }).optional(),
  status: z.enum(JOB_STATUSES, { error: "invalid" }).optional(),
  page: z.coerce
    .number({ error: "invalid" })
    .int({ error: "invalid" })
    .min(1, { error: "out_of_range" })
    .max(100_000, { error: "out_of_range" })
    .default(1),
  pageSize: z.coerce
    .number({ error: "invalid" })
    .int({ error: "invalid" })
    .min(1, { error: "out_of_range" })
    .max(100, { error: "out_of_range" })
    .default(25),
});

export const jobLogQuerySchema = z.object({
  after: z.coerce
    .number({ error: "invalid" })
    .int({ error: "invalid" })
    .min(0, { error: "out_of_range" })
    .default(0),
  limit: z.coerce
    .number({ error: "invalid" })
    .int({ error: "invalid" })
    .min(1, { error: "out_of_range" })
    .max(JOB_LOG_PAGE_MAX, { error: "out_of_range" })
    .default(JOB_LOG_PAGE_MAX),
  level: levelList.optional(),
});

// --- Calibre import ----------------------------------------------------------------

export const calibreLibraryRequestSchema = z.object({
  path: z
    .string({ error: "required" })
    .trim()
    .min(1, { error: "required" })
    .max(4096, { error: "too_long" }),
});

export const calibreImportRequestSchema = calibreLibraryRequestSchema.extend({
  importRatings: z.boolean({ error: "invalid" }).default(false),
  refreshMetadata: z.boolean({ error: "invalid" }).default(false),
});

export interface CalibreLibraryDto {
  path: string;
  /** Null when `metadata.db` could not be read. */
  bookCount: number | null;
  modifiedAt: string;
}

export interface CalibreLibrariesDto {
  root: string;
  libraries: CalibreLibraryDto[];
  /** True when the root does not exist or contains no library: show the mount instructions. */
  hint: boolean;
  /** The active (queued or running) Calibre import, if any. */
  activeJobId: string | null;
}

export interface CalibrePreviewDto {
  path: string;
  totalBooks: number;
  alreadyImported: number;
  toConsider: number;
  booksWithoutFiles: number;
  authors: number;
  series: number;
  tags: number;
  publishers: number;
  languages: number;
  /** Supported formats (lower case) with file counts. */
  formats: Record<string, number>;
  /** Formats Shelflist does not support (upper case as in Calibre) with file counts: skipped. */
  skippedFormats: Record<string, number>;
  missingFiles: number;
  /** Size of the supported format files and covers that would be read. */
  totalBytes: number;
  freeBytes: number;
  insufficientSpace: boolean;
  ratedBooks: number;
  /** The preview never hashes files, so content duplicates are only detected during the import. */
  contentDuplicatesChecked: false;
}

/** Result of a finished (or cancelled/failed) `calibre_import` job. */
export interface CalibreImportResultDto {
  imported: number;
  alreadyImported: number;
  filesAttachedToBooks: number;
  skippedNoFiles: number;
  skippedDuplicateFiles: number;
  failed: number;
  copiedFiles: number;
  copiedBytes: number;
  skippedUnsupportedFiles: number;
  missingFiles: number;
  importedCovers: number;
  importedRatings: number;
  createdAuthors: number;
  createdSeries: number;
  createdTags: number;
  createdPublishers: number;
  /** Already imported books whose metadata was refreshed (absent in results of older jobs). */
  refreshedBooks?: number;
  /** Already imported books whose cover was replaced by a refresh. */
  refreshedCovers?: number;
  /** Info log entries dropped because the log cap was reached. */
  droppedLogEntries?: number;
}

export interface CalibreImportPayload {
  path: string;
  importRatings: boolean;
  /** Missing in payloads stored by older versions: treated as false. */
  refreshMetadata?: boolean;
}
